// What the docked panel shows on the Hub overview and a Question page: the hub's
// market, one question, its pricing, near misses, a diagnosis or a knowledge
// source. Real Curator API figures where the Exchange records them; sample
// values, each marked Sample, where it doesn't yet. See Design > UI, "Workspace".

import { useState } from "preact/hooks";
import { Button, EmptyState, Field, fmtNum, Icon, LeadFigure, LinkButton, MoneyInput, PanelBlock, PillGroup, SampleMark, Select, ShareList, StatusBadge } from "../../../ui/src/index.ts";
import type { ChartTone, FeedbackTone, ShareRow } from "../../../ui/src/index.ts";
import { hubOutcomes, isLive, type IntentActivity } from "../domain/activity.ts";
import { percent, perMillion, usd } from "../domain/money.ts";
import { bestPrice, type PricingRow } from "../domain/pricing.ts";
import { hubSources, priceParts, type Sample, type SourceSample } from "../domain/sample.ts";
import type { DemandItem, DemandResponse, GoldenQuestion, HubSummary, Intent } from "../domain/types.ts";
import { href } from "./router.ts";
import { useStore } from "./store.ts";
import { capitalize } from "./words.ts";

export type PanelMode = "hub" | "question" | "pricing" | "matches" | "diagnose" | "source";
export type Sel = { mode: PanelMode; intent?: string; source?: string };

/** Everything the panel reads, gathered once by the page. */
export type HubData = {
  hub: HubSummary; short: string; sample: Sample;
  activity: Map<string, IntentActivity>; pricing: PricingRow[];
  demand: DemandResponse | null; golden?: Map<string, GoldenQuestion[]>;
  sources: ReturnType<typeof hubSources>;
};

export const intentLabel = (hub: HubSummary, id?: string) => capitalize(hub.manifest.intents.find((i) => i.id === id)?.label ?? id ?? "");
const SOURCE_TONES: Record<SourceSample["state"], FeedbackTone> = { Current: "success", Healthy: "success", Degraded: "warning", Stale: "warning", "Not recorded": "neutral" };
export const SOURCE_TONE = (s: SourceSample["state"]): FeedbackTone => SOURCE_TONES[s];
/** The sources a question's recipe reads: those that name it, or every source when none say. */
export const sourcesFor = (d: HubData, intent: string) => {
  const named = d.sources.filter((s) => s.readBy?.includes(intent));
  return named.length ? named : d.sources.filter((s) => !s.readBy);
};
const floorOf = (sample: Sample, ask: number) => { const p = priceParts(sample, ask); return p.fee + p.inputs; };
const creditsBackOf = (d: HubData, intent: string) => d.sample.hubs[d.short]?.creditsBack?.[intent];

export const PANEL_LABEL: Record<PanelMode, string> = { hub: "Hub summary", question: "Question", pricing: "Pricing", matches: "Near misses", diagnose: "Diagnosis", source: "Knowledge source" };

/** The panel's body for a selection. */
export function PanelBody({ d, sel, go }: { d: HubData; sel: Sel; go: (s: Sel) => void }) {
  const intent = d.hub.manifest.intents.find((i) => i.id === sel.intent);
  const source = d.sources.find((s) => s.name === sel.source);
  return <>
    {sel.mode !== "hub" && <p class="st-panel-label">{PANEL_LABEL[sel.mode]}</p>}
    {sel.mode === "hub" && <HubSummaryPanel d={d} />}
    {sel.mode === "question" && intent && <QuestionSummary d={d} intent={intent} go={go} />}
    {sel.mode === "pricing" && intent && <PricingPanel key={intent.id} d={d} intent={intent} />}
    {sel.mode === "matches" && <MatchesPanel d={d} />}
    {sel.mode === "diagnose" && intent && <DiagnosePanel d={d} intent={intent} go={go} />}
    {sel.mode === "source" && source && <SourcePanel d={d} s={source} go={go} />}
  </>;
}

const OUTCOME_SHARES: [keyof ReturnType<typeof hubOutcomes>["byKey"], string, ChartTone][] = [
  ["detail", "Asked for a detail", "info"], ["declined", "Declined", "muted"], ["planned", "For a planned question", "violet"],
  ["pricedOut", "Priced out", "pink"], ["notFound", "Found nothing", "neutral"], ["error", "Ended in an error", "danger"],
];

/** Try the hub as a consumer would: one of its example questions, asked in the Console's Playground, in a new tab. */
function AskInConsole({ hub }: { hub: HubSummary }) {
  const consoleUrl = useStore((s) => s.config.consoleUrl);
  const example = hub.manifest.intents.find(isLive)?.examples[0];
  return <PanelBlock title="Ask it yourself">
    <p class="st-note">See this hub's answers the way a consumer does{example ? <>: ask “{example}” in the Console's Playground</> : ", in the Console's Playground"}. Asks there charge your Console account.</p>
    <Button size="sm" href={`${consoleUrl}/#/playground`} target="_blank" rel="noreferrer">Ask it in the Console<Icon name="external" size={16} /><span class="ui-sr-only"> (opens in a new tab)</span></Button>
  </PanelBlock>;
}

/** `inline` is the Hub overview's Summary tab: the blocks without the panel's own heading and hint. */
export function HubSummaryPanel({ d, inline = false }: { d: HubData; inline?: boolean }) {
  const stats = d.hub.stats;
  if (!stats) return <>{!inline && <h2>How the market treats this hub</h2>}<p class="st-note">No asks have reached this hub yet.</p><AskInConsole hub={d.hub} /></>;
  const o = hubOutcomes(stats.outcomes);
  const nd = o.notDispatched;
  const reached = o.received + nd.total;
  const inputs = d.sources.filter((s) => s.cost);
  return <>
    {!inline && <><h2>How the market treats this hub</h2>
    <p class="st-note">Select anything on the left to see it here and act on it.</p></>}
    <PanelBlock title="Routing" aside="All asks so far">
      <LeadFigure value={percent(o.received, reached)} label={<>of asks for this subject reached the hub · {fmtNum(o.received)} of {fmtNum(reached)}</>} />
      {nd.total > 0 && <ShareList title="Why the rest didn't" rows={([
        { label: "Didn't match well enough", share: nd.belowThreshold, value: fmtNum(nd.belowThreshold), tone: "neutral" },
        { label: "Another hub ranked higher", share: nd.outranked, value: fmtNum(nd.outranked), tone: "violet" },
        { label: "Below the match cutoff", share: nd.belowCutoff, value: fmtNum(nd.belowCutoff), tone: "muted" },
        { label: "Other reasons", share: nd.other, value: fmtNum(nd.other), tone: "muted" },
      ] satisfies ShareRow[]).filter((r) => r.share > 0)} />}
    </PanelBlock>
    <PanelBlock title="Outcomes" aside="All asks so far">
      <LeadFigure value={fmtNum(o.byKey.answered)} label="answered, and paid for" />
      <ShareList title="Free outcomes" rows={OUTCOME_SHARES.filter(([k]) => o.byKey[k] > 0).map(([k, label, tone]) => ({ label, share: o.byKey[k], value: fmtNum(o.byKey[k]), tone }))} />
      <p class="st-note">Asking for a detail and declining are free. Many declines are asks outside your charter; some are demand worth a look.</p>
    </PanelBlock>
    {(d.sample.channels || d.sample.rails) && <PanelBlock title={<>Where answers were paid<SampleMark /></>} aside="Share of answers">
      {d.sample.channels && <ShareList title="By channel" rows={d.sample.channels.map((c, i) => ({ label: c.label, share: c.share, value: `${c.share}%`, tone: (["info", "cyan", "violet"] as const)[i % 3]! }))} />}
      {d.sample.rails && <ShareList title="By payment" rows={d.sample.rails.map((c, i) => ({ label: c.label, share: c.share, value: `${c.share}%`, tone: (["success", "neutral"] as const)[i % 2]! }))} />}
    </PanelBlock>}
    {inputs.length > 0 && <PanelBlock title={<>Inputs your recipes call<SampleMark /></>}>
      <ul class="st-rows">{inputs.map((s) => <li key={s.name}><span>{s.name}</span><span class="st-note">{s.cost}</span></li>)}</ul>
    </PanelBlock>}
    <AskInConsole hub={d.hub} />
  </>;
}

function QuestionSummary({ d, intent, go }: { d: HubData; intent: Intent; go: (s: Sel) => void }) {
  const a = d.activity.get(intent.id);
  const parts = priceParts(d.sample, intent.askUsdPerMillion);
  const credits = creditsBackOf(d, intent.id);
  const golden = d.golden?.get(intent.id)?.length;
  return <>
    <h2>{capitalize(intent.label)}</h2>
    {intent.examples[0] && <p class="st-pattern">“{intent.examples[0]}”</p>}
    <div class="st-row-actions"><Button size="sm" variant="solid" href={href({ name: "question", hub: d.hub.hub.id, intent: intent.id })}>Open the question</Button></div>
    <PanelBlock title="Price" aside={isLive(intent) ? <Button size="sm" onClick={() => go({ mode: "pricing", intent: intent.id })}>Edit margin</Button> : undefined}>
      <dl class="st-price">
        <div><dt>Delivery fee<SampleMark /></dt><dd>{perMillion(parts.fee)}</dd></div>
        <div><dt>Inputs<SampleMark /></dt><dd>{perMillion(parts.inputs)}</dd></div>
        <div class="st-price__mine"><dt>Your margin</dt><dd>{perMillion(parts.margin)}</dd></div>
        <div class="st-price__total"><dt>Price per million answers</dt><dd>{perMillion(intent.askUsdPerMillion)}</dd></div>
      </dl>
    </PanelBlock>
    <PanelBlock title="In the newest events">
      {a ? <dl class="st-facts">
        <div><dt>Answered</dt><dd>{fmtNum(a.answered)} of {fmtNum(a.asked)}</dd></div>
        <div><dt>Revenue</dt><dd>{a.revenueUsd ? usd(a.revenueUsd) : "–"}</dd></div>
        <div><dt>Priced out</dt><dd>{fmtNum(a.byKey.pricedOut)}</dd></div>
      </dl> : <p class="st-note">No asks for this question in the newest events.</p>}
    </PanelBlock>
    <PanelBlock title="Knowledge it reads">
      <ul class="st-rows st-rows--link">{sourcesFor(d, intent.id).map((s) => <li key={s.name} onClick={() => go({ mode: "source", source: s.name })}><span><span class="st-note">{s.type}</span> {s.name}</span><StatusBadge tone={SOURCE_TONE(s.state)}>{s.state}</StatusBadge></li>)}</ul>
    </PanelBlock>
    <PanelBlock title="Quality" aside={<Button size="sm" href={href({ name: "golden", hub: d.hub.hub.id })}>Golden questions</Button>}>
      <dl class="st-facts">
        {credits !== undefined && <div><dt>Credits back<SampleMark /></dt><dd>{fmtNum(credits)}</dd></div>}
        <div><dt>Golden questions</dt><dd>{golden === undefined ? "–" : fmtNum(golden)}</dd></div>
      </dl>
    </PanelBlock>
  </>;
}

/** The margin editor: the price follows the margin, and the hub's own recent bids project what a new price would have earned. */
function PricingPanel({ d, intent }: { d: HubData; intent: Intent }) {
  const row = d.pricing.find((r) => r.intent === intent.id);
  const ask = intent.askUsdPerMillion;
  const floor = floorOf(d.sample, ask);
  const margin0 = Math.max(0, ask - floor);
  const best = row ? bestPrice(row.answeredBids, row.pricedOutBids, ask, floor) : null;
  const suggested = best ? Math.max(0, best.price - floor) : null;
  const [margin, setMargin] = useState(String(margin0));
  const tries = [...new Set([margin0, ...(suggested !== null ? [suggested] : [])])];
  const m = Math.max(0, Number(margin) || 0);
  const price = floor + m;
  const bids = row ? [...row.answeredBids, ...row.pricedOutBids] : [];
  const wouldAnswer = bids.filter((b) => b >= price).length;
  const lead = !row || row.signal === "quiet" ? "Too few asks in the newest events to say how the price is doing."
    : row.signal === "pricedOut" ? `${fmtNum(row.pricedOutBids.length)} asks were priced out and didn't come back, at bids from ${perMillion(Math.min(...row.pricedOutBids))} to ${perMillion(Math.max(...row.pricedOutBids))}. Your price is ${perMillion(ask)}.`
    : row.signal === "room" && row.medianAnsweredBid !== null ? `Answered asks bid a median of ${perMillion(row.medianAnsweredBid)}, ${(row.medianAnsweredBid / ask).toFixed(1)}× your price, so there may be room to raise it.`
    : row.signal === "preset" ? "Most answered asks bid exactly the same amount, as a preset bid would, so they don't say much about price."
    : "Your price holds: asks are answered and none were priced out.";
  return <>
    <h2>{capitalize(intent.label)}</h2>
    <p class="st-lead">{lead}</p>
    <PanelBlock title="Set your margin">
      <dl class="st-price">
        <div><dt>Delivery fee and inputs<SampleMark /></dt><dd>{perMillion(floor)}</dd></div>
        <div class="st-price__mine"><dt>Your margin</dt><dd><MoneyInput aria-label="Your margin, dollars per million answers" class="st-price__input" value={margin} onInput={(e) => setMargin(e.currentTarget.value)} /></dd></div>
        <div class="st-price__total"><dt>New price</dt><dd>{perMillion(price)}</dd></div>
      </dl>
      <div class="st-chips"><span class="st-note">Try</span>
        <PillGroup size="sm" label="Try a margin" value={tries.find((v) => v === m) === undefined ? "" : String(m)} onChange={setMargin}
          options={tries.map((v) => ({ value: String(v), label: `${perMillion(v)}${v === margin0 ? " · now" : " · suggested"}` }))} />
      </div>
      {bids.length > 0 && <p class="st-projection">{m === margin0
        ? <>In the newest events your price answered {fmtNum(row?.answered ?? 0)} asks.</>
        : <>At {perMillion(price)}, {fmtNum(wouldAnswer)} of the {fmtNum(bids.length)} asks in the newest events bid enough, earning about <strong>{usd((wouldAnswer * price) / 1e6)}</strong> against {usd(((row?.answered ?? 0) * ask) / 1e6)} now.</>}</p>}
      <p class="st-note">You set the margin; the price follows. A higher margin invites other hubs to answer for less.</p>
    </PanelBlock>
    <div class="st-row-actions"><Button variant="solid" aria-disabled="true" title="Changing a price isn't connected yet">Save margin</Button><span class="st-note">Changing a price isn't connected yet.</span></div>
  </>;
}

type Verdict = "matched" | "dismissed" | "new";
function verdictText(v: Verdict, question: string) {
  switch (v) {
    case "matched": return <><StatusBadge tone="info">Pending retrain</StatusBadge> Added as an example of “{question}”.</>;
    case "dismissed": return "Marked as not a match. The hub learns from this too.";
    case "new": return "Opened as a new question draft.";
    default: return v satisfies never;
  }
}
/** Asks the hub declined or just missed: confirm the ones it should answer. */
function MatchesPanel({ d }: { d: HubData }) {
  const live = d.hub.manifest.intents.filter(isLive);
  const [done, setDone] = useState<Record<string, Verdict>>({});
  const [target, setTarget] = useState<Record<string, string>>({});
  if (!d.demand) return <><h2>Asks that almost matched</h2><p class="st-note">Reading the hub's demand…</p></>;
  const items: (DemandItem & { group: string })[] = [
    ...d.demand.missed.items.map((i) => ({ ...i, group: "Just missed the hub" })),
    ...d.demand.declined.items.map((i) => ({ ...i, group: "Declined by the hub" })),
  ].sort((a, b) => b.count * b.bidUsd - a.count * a.bidUsd).slice(0, 8);
  return <>
    <h2>Asks that almost matched</h2>
    <p class="st-lead">Asks this hub declined or just missed in the last 30 days. Tell it which ones it should answer, and it learns to recognise them.</p>
    {items.length === 0 ? <p class="st-note">None in the last 30 days.</p> : <ul class="st-misses">{items.map((n) => {
      const key = n.question;
      const chosen = target[key] ?? live[0]?.id ?? "";
      return <li key={key}>
        <span class="st-misses__q">“{n.question}”</span>
        <span class="st-note">{n.group} · asked {fmtNum(n.count)} {n.count === 1 ? "time" : "times"}{n.bidUsd ? ` · offered ${usd(n.bidUsd)}` : ""}</span>
        {n.reason && <span class="st-note">The hub said: {n.reason}</span>}
        {done[key]
          ? <span class="st-misses__done">{verdictText(done[key], intentLabel(d.hub, chosen))}<SampleMark title="Adding examples isn't connected yet: this shows what would happen" /></span>
          : <div class="st-misses__act">
            {live.length > 0 && <Field id={`miss-${key.length}-${n.count}`} label="Matches">{(attrs) => <Select {...attrs} value={chosen} onChange={(e) => setTarget({ ...target, [key]: e.currentTarget.value })}>{live.map((i) => <option key={i.id} value={i.id}>{capitalize(i.label)}</option>)}</Select>}</Field>}
            <span class="st-row-actions"><Button size="sm" onClick={() => setDone({ ...done, [key]: "matched" })}>Add as example</Button><Button size="sm" variant="ghost" onClick={() => setDone({ ...done, [key]: "dismissed" })}>Doesn't match</Button><Button size="sm" variant="ghost" onClick={() => setDone({ ...done, [key]: "new" })}>New question</Button></span>
          </div>}
      </li>;
    })}</ul>}
  </>;
}

function DiagnosePanel({ d, intent, go }: { d: HubData; intent: Intent; go: (s: Sel) => void }) {
  const credits = creditsBackOf(d, intent.id) ?? 0;
  const a = d.activity.get(intent.id);
  const bad = sourcesFor(d, intent.id).filter((s) => s.state === "Degraded" || s.state === "Stale");
  return <>
    <h2>{capitalize(intent.label)}</h2>
    <p class="st-lead">{fmtNum(credits)} credits back<SampleMark /> on {fmtNum(a?.answered ?? 0)} answers in the newest events. Is it the knowledge, or the matching?</p>
    <div class="st-fork">
      <div class="st-fork__case" data-likely={bad.length ? "" : undefined}>
        <p class="st-fork__tag">{bad.length > 0 && <StatusBadge tone="warning">Likely</StatusBadge>} A knowledge problem</p>
        <p>The answers were wrong or stale, not the matching.</p>
        <ul>
          {bad.map((s) => <li key={s.name}><LinkButton onClick={() => go({ mode: "source", source: s.name })}>{s.name}</LinkButton> is {s.state.toLowerCase()}: {s.detail}.</li>)}
          {bad.length === 0 && <li>No knowledge source this question reads is degraded or stale.</li>}
          <li>Golden questions check its answers: run them to see whether they still pass.</li>
        </ul>
        <div class="st-row-actions"><Button size="sm" variant="solid" href={href({ name: "golden", hub: d.hub.hub.id })}>Run golden questions</Button></div>
      </div>
      <div class="st-fork__case">
        <p class="st-fork__tag">Or a matching problem</p>
        <p>The hub matched asks this question was never meant to answer. Read its recent asks for ones outside the charter.</p>
        <div class="st-row-actions"><Button size="sm" href={href({ name: "question", hub: d.hub.hub.id, intent: intent.id })}>See its recent asks</Button></div>
      </div>
    </div>
    <p class="st-note">Credits back come out of pending earnings, never from money already paid out to you.</p>
  </>;
}

function SourcePanel({ d, s, go }: { d: HubData; s: ReturnType<typeof hubSources>[number]; go: (s: Sel) => void }) {
  const readers = d.hub.manifest.intents.filter((i) => isLive(i) && (s.readBy ? s.readBy.includes(i.id) : true));
  return <>
    <h2>{s.name}</h2>
    <p class="st-flat"><span class="st-note">{s.type}</span> <StatusBadge tone={SOURCE_TONE(s.state)}>{s.state}</StatusBadge>{s.sample && <SampleMark />}</p>
    <dl class="st-facts">
      <div><dt>{s.type === "API" ? "Health" : "Freshness"}</dt><dd>{s.detail}</dd></div>
      {s.cost && <div><dt>Cost</dt><dd>{s.cost}</dd></div>}
    </dl>
    <PanelBlock title="Questions that read it">
      <ul class="st-rows st-rows--link">{readers.map((i) => <li key={i.id} onClick={() => go({ mode: "question", intent: i.id })}><span>{capitalize(i.label)}</span></li>)}</ul>
    </PanelBlock>
    {(s.state === "Degraded" || s.state === "Stale") && <p class="st-note">When a source degrades, answers that read it can go wrong, and credits back follow. Fixing the source is usually faster than repricing.</p>}
  </>;
}

/** The knowledge sources as a small table: what each is, how current, which questions read it. Selecting one opens it in the panel. */
export function SourcesTable({ d, sel, go, intent }: { d: HubData; sel: Sel; go: (s: Sel) => void; intent?: string }) {
  const live = d.hub.manifest.intents.filter(isLive).length;
  const rows = intent ? sourcesFor(d, intent) : d.sources;
  const sampled = rows.some((s) => s.sample);
  if (rows.length === 0) return <div class="st-sources"><EmptyState>{intent ? "The hub's toolset doesn't list what this question reads yet." : "The hub's toolset doesn't list any knowledge sources yet."}</EmptyState></div>;
  return <div class="st-sources">
    <p class="st-caption">Knowledge sources{intent ? " this question reads" : ""}</p>
    <div class="ui-table-wrap"><table class="ui-table st-table">
      <thead><tr><th class="ui-table__fill">Source</th><th style={{ width: 96 }}>Type</th><th style={{ width: "34%" }}>Freshness or health{sampled && <SampleMark />}</th>{!intent && <th class="ui-table__num" style={{ width: 80 }}>Read by</th>}<th style={{ width: 120 }}>Status</th></tr></thead>
      <tbody>{rows.map((s) => <tr key={s.name} data-selected={sel.mode === "source" && sel.source === s.name ? "" : undefined} onClick={() => go({ mode: "source", source: s.name })}>
        <td class="ui-table__fill"><span class="st-cell-text"><strong>{s.name}</strong></span></td>
        <td class="st-note">{s.type}</td>
        <td><span class="st-cell-text st-note">{s.detail}</span></td>
        {!intent && <td class="ui-table__num">{s.readBy ? `${s.readBy.length} of ${live}` : "All"}</td>}
        <td><StatusBadge tone={SOURCE_TONE(s.state)}>{s.state}</StatusBadge></td>
      </tr>)}</tbody>
    </table></div>
  </div>;
}
